/**
 * 시험지 화면 왼쪽 폴더 칸의 **폭**을 브라우저 세션이 쥔다 (sessionStorage).
 *
 * 전에는 `w-56`(224px) 고정이었다. 폴더 이름이 길면 줄마다 말줄임이 걸려, 무엇이 무엇인지
 * 알아보려면 [⋯] 를 열거나 마우스를 올려야 했다. 그래서 칸 오른쪽 끝을 끌어 넓힐 수 있게 하고,
 * 넓힌 폭을 기억한다(오너 지시 2026-09-10 — 「세션 스토리지에 해당 폭 변경사항은 저장하면 됨」).
 *
 * 형제 folderOpenState.js 와 **같은 규약**이다 — 접두어 + 판 번호 + 계정. 계정을 키에 넣는 이유도 같다:
 * 같은 탭에서 계정을 바꿔 로그인해도 앞사람이 넓혀 둔 칸을 물려받지 않게.
 * 세션인 이유도 같다 — 탭을 닫으면 사라지고 새 탭은 기본 폭으로 시작한다.
 *
 * 폭의 범위:
 *  - **최소 224px** — 옛 고정 폭이다. 이보다 좁히지 않는다. 트리 머리줄(「폴더 · 새로고침 · 새 폴더」)과
 *    범위 스위치(「전체 · 내 폴더」)가 이 폭에 맞춰 짜여 있어, 더 좁히면 버튼이 줄을 넘어간다.
 *  - **최대 480px** — 옆의 시험지 목록 표가 쓸 자리를 남긴다. 표는 열이 많아 좁아지면 먼저 무너진다.
 *
 * 이 파일은 어떤 경우에도 던지지 않는다(folderOpenState 와 같은 태도). 저장소 접근 자체가 예외인
 * 환경에서도 끌어 넓히는 것은 그 자리에서 되고, 다음에 기본 폭으로 시작할 뿐이다.
 */

const PREFIX = 'examPapers:folderWidth:v1:';
const VERSION = 1;

export const FOLDER_WIDTH = Object.freeze({
  min: 224,
  max: 480,
  // 처음 열 때의 폭 — 옛 고정 폭 그대로다. 아무도 끌지 않으면 화면이 한 픽셀도 바뀌지 않아야 한다.
  initial: 224,
  // 키보드(← →)로 한 번에 움직이는 양
  step: 16,
});

const keyOf = (userId) => `${PREFIX}${userId || 'anon'}`;

/** 범위 안으로 조이고 정수로 반올림한다. 숫자가 아니면 기본 폭 */
export function clampFolderWidth(px) {
  const n = Number(px);
  if (px == null || px === '' || !Number.isFinite(n)) return FOLDER_WIDTH.initial;
  return Math.round(Math.min(FOLDER_WIDTH.max, Math.max(FOLDER_WIDTH.min, n)));
}

/**
 * 저장된 폭 — 없거나 망가졌으면 기본 폭. 저장된 값도 **다시 조인다**:
 * 범위가 나중에 바뀌어도 옛 세션 값이 새 범위를 뚫고 나오지 않게.
 * @returns {number}
 */
export function readFolderWidth(userId) {
  try {
    const raw = sessionStorage.getItem(keyOf(userId));
    if (!raw) return FOLDER_WIDTH.initial;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== VERSION) return FOLDER_WIDTH.initial;
    return clampFolderWidth(parsed.w);
  } catch (_) {
    return FOLDER_WIDTH.initial;
  }
}

/**
 * 폭을 저장한다 — 끄는 동안 매번이 아니라 **놓을 때 한 번** 부른다(부르는 쪽 몫).
 * @returns {boolean} 저장에 성공했는지. 안 봐도 된다.
 */
export function writeFolderWidth(userId, px) {
  try {
    sessionStorage.setItem(keyOf(userId), JSON.stringify({ v: VERSION, w: clampFolderWidth(px) }));
    return true;
  } catch (_) {
    return false;
  }
}
